import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import { API_URLS } from "src/utils/API_URLS";
import { parseErrorMessages, showErrorToast } from "src/utils/methods";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Tab from "src/app/components/Tabs/Tab";
import Tabs from "src/app/components/Tabs/Tabs";
import { Button } from "src/app/components/Button/Button";
import { AvatarAndName } from "src/app/components/GlobalTaskDetails/tabs/GlobalActivityTab/GlobalActivityTabCampaigns";
import { ReactComponent as InfoIcon } from "src/images/info-circle-blue.svg";
import { ReactComponent as WarningIcon } from "src/images/warning-orange.svg";
import placeholderIcon from "src/images/placeholder-image.svg";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import { EmailContent, MessageDetails, Steps } from "./types";
import { EmailPreview } from "./EmailPreview";

enum ActiveTab {
  CREATOR,
  BCC,
  CC,
  PREVIEW,
}

interface CreatorMessagePaneConfirmEmailProps {
  activeWorkspaceUuid: string;
  emailContent: EmailContent;
  messageDetails: MessageDetails;
  hidePane: () => void;
  setStep: React.Dispatch<React.SetStateAction<Steps>>;
  setIsSendingMessage: React.Dispatch<React.SetStateAction<boolean>>;
  projectId: string;
}

export default function CreatorMessagePaneConfirmEmail({
  activeWorkspaceUuid,
  emailContent,
  messageDetails,
  hidePane,
  setStep,
  setIsSendingMessage,
  projectId,
}: CreatorMessagePaneConfirmEmailProps) {
  const [activeTab, setActiveTab] = useState(ActiveTab.CREATOR);
  const [isMessageValidated, setIsMessageValidated] = useState<
    boolean | undefined
  >();
  const [validationLoading, setValidationLoading] = useState(true);

  const {
    replyToEmail,
    includeInCcCampaignMembers,
    includeInBccCampaignMembers,
    subject,
    message,
    recipients,
    ccRecipients,
    bccRecipients,
  } = emailContent;

  const recipientsWithEmails = recipients.filter(
    (recipient) => recipient.emailFromCrm,
  );

  const disableSendButton = !isMessageValidated || validationLoading;

  const handleSendEmail = async () => {
    setIsSendingMessage(true);
    try {
      await axios.post(API_URLS.sendMessage, {
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsTasksUuids: recipientsWithEmails.map(
          (recipient) => recipient.wsTaskUuid,
        ),
        replyToEmail,
        ccCampaignMembersUuids: ccRecipients.map((recipient) => recipient.id),
        bccCampaignMembersUuids: bccRecipients.map((recipient) => recipient.id),
        subject,
        message,
      });
      hidePane();
      toast.success(
        <IDHFormattedMessage
          id="ws_your_message_has_been_sent"
          defaultMessage="Your message has been sent"
        />,
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_an_error_occurred_while_sending_message",
          defaultMessage:
            "Something went wrong. Please try sending the message again.",
        });
      }
      console.error(error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const renderContent = () => {
    if (activeTab === ActiveTab.PREVIEW) {
      return <EmailPreview emailContent={emailContent} />;
    }

    return (
      <>
        <div className="creator-message-pane__cols">
          <div className="creator-message-pane__header">
            {activeTab === ActiveTab.CREATOR ? (
              <IDHFormattedMessage id="ws_creator" defaultMessage="Creator" />
            ) : (
              <IDHFormattedMessage id="ws_member" defaultMessage="Member" />
            )}
          </div>
          <div className="creator-message-pane__header">
            <IDHFormattedMessage id="ws_email" defaultMessage="Email" />
          </div>
        </div>
        <div className="creator-message-pane__emails-list">
          {activeTab === ActiveTab.CREATOR &&
            recipientsWithEmails.map((recipient) => (
              <div
                className="creator-message-pane__cols"
                key={recipient.wsTaskUuid}
              >
                <AvatarAndName
                  name={recipient.title}
                  key={recipient.wsTaskUuid}
                  avatar={recipient.avatar}
                  placeholder={placeholderIcon}
                />
                <div className="creator-message-pane__email-address">
                  {recipient.emailFromCrm}
                </div>
              </div>
            ))}
          {activeTab === ActiveTab.CC &&
            ccRecipients.map((member) => (
              <div className="creator-message-pane__cols" key={member.id}>
                <AvatarAndName
                  name={member.name}
                  avatar={member.avatarUrl}
                  placeholder={placeholderIcon}
                />
                <div className="creator-message-pane__email-address">
                  {member.email}
                </div>
              </div>
            ))}
          {activeTab === ActiveTab.BCC &&
            bccRecipients.map((member) => (
              <div className="creator-message-pane__cols" key={member.id}>
                <AvatarAndName
                  name={member.name}
                  avatar={member.avatarUrl}
                  placeholder={placeholderIcon}
                />
                <div className="creator-message-pane__email-address">
                  {member.email}
                </div>
              </div>
            ))}
        </div>
      </>
    );
  };

  const displayPreviewWarningIcon =
    !validationLoading && isMessageValidated === false;

  useEffect(() => {
    const validateMessage = async () => {
      const wsTasksUuids = recipients.map((recipient) => recipient.wsTaskUuid);

      try {
        const { data: response } = await axios.post(API_URLS.validateMessage, {
          message,
          subject,
          wsTasksUuids,
          wsWorkspaceUuid: activeWorkspaceUuid,
          wsProjectUuid: projectId,
        });

        setIsMessageValidated(response?.content?.valid);
      } catch (error) {
        showErrorToast();
      } finally {
        setValidationLoading(false);
      }
    };

    validateMessage();
  }, [projectId, recipients, activeWorkspaceUuid]);

  return (
    <>
      <div className="creator-message-pane__manage-emails">
        <div className="creator-message-pane__info">
          <InfoIcon />
          <div className="creator-message-pane__info-box">
            <div className="creator-message-pane__info-title">
              <IDHFormattedMessage id="ws_info" defaultMessage="Info" />
            </div>
            {messageDetails.dataProcessingInformation ? (
              <div className="creator-message-pane__info-text">
                <p>
                  <IDHFormattedMessage
                    id="ws_the_following_information_will_be_included_in_the_footer"
                    defaultMessage="The following information will be included in the footer of the email"
                  />
                  :
                </p>

                <p>{messageDetails.dataProcessingInformation}</p>
              </div>
            ) : (
              <div className="creator-message-pane__info-text">
                <IDHFormattedMessage
                  id="ws_no_data_processing_information"
                  defaultMessage={`By clicking "Send," I declare that I voluntarily waive the opportunity to use the tool that allows the user to review information regarding personal data, and I accept any potential responsibility for failing to fulfill this obligation.`}
                />
              </div>
            )}
          </div>
        </div>

        <Tabs>
          <Tab
            tabText={
              <IDHFormattedMessage id="ws_creator" defaultMessage="Creator" />
            }
            active={activeTab === ActiveTab.CREATOR}
            onClick={() => setActiveTab(ActiveTab.CREATOR)}
            count={recipientsWithEmails.length}
          />
          {includeInBccCampaignMembers && bccRecipients.length > 0 && (
            <Tab
              tabText={<IDHFormattedMessage id="ws_bcc" defaultMessage="BCC" />}
              active={activeTab === ActiveTab.BCC}
              onClick={() => setActiveTab(ActiveTab.BCC)}
              count={bccRecipients.length}
            />
          )}
          {includeInCcCampaignMembers && ccRecipients.length > 0 && (
            <Tab
              tabText={<IDHFormattedMessage id="ws_cc" defaultMessage="CC" />}
              active={activeTab === ActiveTab.CC}
              onClick={() => setActiveTab(ActiveTab.CC)}
              count={ccRecipients.length}
            />
          )}
          <Tab
            tabText={
              <IDHFormattedMessage id="ws_preview" defaultMessage="Preview" />
            }
            tabIcon={displayPreviewWarningIcon && <WarningIcon />}
            active={activeTab === ActiveTab.PREVIEW}
            onClick={() => setActiveTab(ActiveTab.PREVIEW)}
          />
        </Tabs>

        {renderContent()}
      </div>

      <div className="creator-message-pane__footer">
        <Button
          size="large"
          variant="white-with-light-grey-border"
          onClick={() => setStep(Steps.CREATE_EMAIL)}
        >
          <IDHFormattedMessage id="ws_back" defaultMessage="Back" />
        </Button>

        <TooltipPortal
          contentClassName="creator-message-pane__footer-tooltip"
          content={
            <IDHFormattedMessage
              id="ws_some_fields_in_your_message_are_missing_values_please_complete_values_before_sending"
              defaultMessage="Some fields in your message are missing values. Please complete values before sending."
            />
          }
          contentHidden={!disableSendButton}
          rightTop
        >
          <Button
            variant="blue"
            size="large"
            onClick={handleSendEmail}
            disabled={disableSendButton}
          >
            <IDHFormattedMessage id="ws_send" defaultMessage="Send" />
          </Button>
        </TooltipPortal>
      </div>
    </>
  );
}
